import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import "./styled.css";
// componentes V3
import StepIndex from "./stepIndex";
import PreForm from "./PreForm";
import InfoPhoto from "./InfoPhoto";
import TakePhoto from "./TakePhoto";
import SuccessMessage from "./SuccessMessage";
import AlreadyRegistered from "./AlreadyRegistered";
import LanguageSelection from "../FaceCollectorAwsV2Page/LanguageSelection";
// serviços e utils
import { getDic } from "../../assets/i18n/dictionary";
import { store } from "../../store";
import * as actions from "../../store/actions";
import { Alert, Loading, IsLoadingBackdrop } from "../../components";
import faceApiService from "../../services/faceApiService";
import fileAwsService from "../../services/fileAwsService";
import faceScanConfigService from "../../services/faceScanConfigService";
import faceAwsService from "../../services/faceAwsService";
import urlUtils from "../../utils/urlUtils";
import moment from "moment";

// Retorna a largura e altura atuais da janela do navegador
function getCurrentDimension() {
  return {
    width: window.innerWidth,
    height: window.innerHeight,
  };
}

export default function FaceCollectorAwsV3Page() {
  const navigate = useNavigate();
  // Parâmetros de URL (ex: eventId, guestId, public, lang)
  const params = urlUtils.getSearchParams();

  // Controla em qual etapa o fluxo está: 1=InfoPhoto, 2=TakePhoto, 3=AlreadyRegistered
  const [step, setStep] = useState(1);
  // Força re-renderização do seletor de idioma quando o idioma muda
  const [reRender, setReRender] = useState(true);
  // Exibe o backdrop de carregamento inicial enquanto os dados da página são buscados
  const [isLoading, setIsLoading] = useState(true);
  // Dimensões da janela, atualizadas em tempo real para componentes responsivos
  const [dimensions, setDimensions] = useState(getCurrentDimension());
  // Controla o loading overlay com mensagem personalizada (ex: "Carregando", "Salvando")
  const [loadingControl, setLoadingControl] = useState({
    open: true,
    message: getDic("carregando"),
  });
  // Configurações visuais e de negócio vindas do backend (cores, eventId, atributo de foto, etc.)
  const [configOptions, setConfigOptions] = useState({
    backgroundImage: "",
    backgroundColor: "#f2f2f2",
    textColor: "#000000",
    buttonBackgroundColor: "#456091",
    buttonTextColor: "#ffffff",
    eventId: "",
    miceContractId: "",
    photoAttributeId: "",
  });
  // Controla a exibição e o conteúdo do componente Alert (título, mensagem, ícone)
  const [alertControl, setAlertControl] = useState({
    open: false,
    title: "",
    message: "",
    icon: "info",
  });
  // Dados do participante (eventId, guestId) e flag para exibir o PreForm quando não vierem pela URL
  const [faceScanControl, setFaceScanControl] = useState({
    eventId: "",
    guestId: "",
    openPreForm: false,
  });

  // Inicializa a página: valida autenticação, define idioma, verifica registro existente e carrega configurações
  const getPageData = useCallback(async () => {
    try {
      // Redireciona para login se o usuário não estiver autenticado e o acesso não for público
      if (
        (!store.getState().user.id || !store.getState().user.email) &&
        params?.public !== "true"
      ) {
        navigate("/Login", { replace: true });
      }

      getLanguage();

      const _faceScanControl = getPreForm();
      await tryPreSearchCurrentDetection(_faceScanControl);
      await getConfig(_faceScanControl);
    } catch (error) {
      console.log("Error getPageData", error);
      setAlertControl({
        open: true,
        title: getDic("erro"),
        message: getDic("enviado-erro"),
        icon: "error",
      });
    } finally {
      setLoadingControl((prev) => ({ ...prev, open: false }));
      setIsLoading(false);
    }

    // Define o idioma da aplicação com base no parâmetro "lang" da URL; padrão: pt-br
    function getLanguage() {
      const _lang = params?.lang || params?.Lang || "";
      switch (_lang) {
        case "en":
          store.dispatch(actions.setLanguage({ lang: _lang }));
          break;
        case "sp":
          store.dispatch(actions.setLanguage({ lang: _lang }));
          break;
        default:
          store.dispatch(actions.setLanguage({ lang: "pt-br" }));
          break;
      }
    }

    // Extrai eventId e guestId da URL; abre o PreForm se algum deles estiver ausente
    function getPreForm() {
      try {
        let _faceScanControl = { eventId: "", guestId: "", openPreForm: false };

        // Acesso público sem IDs na URL é inválido — redireciona para login
        if (
          params?.public === "true" &&
          (!params?.eventId || !params?.guestId)
        ) {
          navigate("/Login", { replace: true });
        }

        if (params?.eventId) _faceScanControl.eventId = params.eventId;
        if (params?.guestId) _faceScanControl.guestId = params.guestId;

        if (!_faceScanControl.eventId || !_faceScanControl.guestId) {
          _faceScanControl.openPreForm = true;
        }

        setFaceScanControl(_faceScanControl);
        return _faceScanControl;
      } catch (error) {
        console.log("Error getPreForm", error);
        throw error;
      }
    }

    // Verifica se o participante já possui uma face cadastrada; se sim, pula direto para o step 3
    async function tryPreSearchCurrentDetection(faceScanControlData) {
      try {
        if (faceScanControlData.eventId && faceScanControlData.guestId) {
          const _hasDetection = await faceAwsService.checkIfGuestHasRegister(
            faceScanControlData.eventId,
            faceScanControlData.guestId,
          );
          if (_hasDetection) {
            setStep(3);
          }
        }
      } catch (error) {
        console.log("Error tryPreSearchCurrentDetection", error);
      }
    }

    // Busca as configurações visuais e de negócio do evento no backend
    async function getConfig(faceScanControlData) {
      if (!faceScanControlData.eventId) return;
      try {
        const _ret = await faceScanConfigService.getByEventId(
          faceScanControlData.eventId,
          "mice",
        );
        if (_ret.id) {
          setConfigOptions(_ret);
        }
      } catch (error) {
        console.log("Error getConfig", error);
      }
    }
  }, []);

  // Valida os campos do PreForm e, se OK, fecha o formulário para abrir a câmera
  const tryOpenCamera = useCallback(async () => {
    try {
      const _errorMessage = validateForm();
      if (_errorMessage) {
        setAlertControl({
          open: true,
          title: "",
          message: _errorMessage,
          icon: "danger",
        });
        return;
      }

      setFaceScanControl((prev) => ({ ...prev, openPreForm: false }));
    } catch (error) {
      console.log("Error tryOpenCamera", error);
      setAlertControl({
        open: true,
        title: getDic("erro"),
        message: getDic("enviado-erro"),
        icon: "error",
      });
    }

    // Retorna mensagem de erro se eventId ou guestId estiverem vazios
    function validateForm() {
      if (!faceScanControl.eventId) {
        return `${getDic("campo")} "ID ${getDic("evento")}" ${getDic("obrigatorio")}`;
      }
      if (!faceScanControl.guestId) {
        return `${getDic("campo")} "ID ${getDic("participante")}" ${getDic("obrigatorio")}`;
      }
    }
  }, [faceScanControl]);

  // Exibe alert de erro específico para falha no arquivo de imagem capturado
  const openMessageFileError = useCallback(() => {
    setAlertControl({
      open: true,
      title: getDic("erro"),
      message: getDic("face-collector-step-2-g"),
      icon: "error",
    });
  }, []);

  // Recebe a imagem capturada, faz o upload para o S3 e avança para o step de sucesso
  const tryCaptureFace = useCallback(
    async (image = "") => {
      try {
        setLoadingControl((prev) => ({
          ...prev,
          open: true,
          message: getDic("salvando"),
        }));

        // Envia a imagem (base64 sem o prefixo data:...) para o serviço de reconhecimento facial
        const _errorMessage = await faceAwsService.uploadFileToS3(
          faceScanControl.eventId,
          faceScanControl.guestId,
          image.split(",")[1],
        );

        if (_errorMessage) {
          setAlertControl({
            open: true,
            title: "",
            message: _errorMessage,
            icon: "danger",
          });
          return;
        }

        await trySaveImageLog(image);

        // Avança para a tela de confirmação de cadastro realizado
        setStep(3);

        // Notifica o frame pai (ex: app externo via iframe) que a face foi coletada com sucesso
        window.top.postMessage(
          JSON.stringify({ message: "faceDetectionCollected", data: "" }),
          "*",
        );
      } catch (error) {
        console.log("Error tryCaptureFace", error);
        setAlertControl({
          open: true,
          title: getDic("erro"),
          message: getDic("enviado-erro"),
          icon: "error",
        });
      } finally {
        setLoadingControl((prev) => ({ ...prev, open: false }));
      }

      // Salva a imagem no S3 e atualiza o atributo de foto do participante no sistema MICE
      // Só executa se o evento tiver photoAttributeId e miceContractId configurados
      async function trySaveImageLog(imageData) {
        try {
          if (
            !configOptions.photoAttributeId ||
            !configOptions.miceContractId ||
            configOptions.eventId !== faceScanControl.eventId
          ) {
            return;
          }

          const _awsToken = await fileAwsService.getToken();
          const _fileUrl = await fileAwsService.uploadFileToS3AndCreateMetadata(
            _awsToken,
            parseInt(configOptions.miceContractId),
            faceScanControl.guestId,
            imageData,
          );

          // Gera uma URL assinada com validade de 6 dias para leitura da imagem
          const _urlArray = _fileUrl.split("/");
          const _url = await fileAwsService.getFileUrl(
            _awsToken,
            `${_urlArray[3]}/${_urlArray[4]}`,
            _urlArray[5].split("?")[0],
            moment().add(6, "days").toISOString(),
          );

          // Persiste a URL da foto no atributo configurado do participante
          await faceApiService.updatePhotoAttributeValue(
            faceScanControl.guestId,
            configOptions.photoAttributeId,
            _url,
          );
        } catch (error) {
          console.log("Error trySaveImageLog", error);
        }
      }
    },
    [faceScanControl, configOptions],
  );

  function lightenHex(hex, amount = 30) {
    const num = parseInt(hex.replace("#", ""), 16);
    const r = Math.min(255, Math.max(0, (num >> 16) + amount));
    const g = Math.min(255, Math.max(0, ((num >> 8) & 0xff) + amount));
    const b = Math.min(255, Math.max(0, (num & 0xff) + amount));
    return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
  }

  // Atualiza as dimensões da janela ao redimensionar para manter componentes responsivos
  useEffect(() => {
    const updateDimension = () => setDimensions(getCurrentDimension());
    window.addEventListener("resize", updateDimension);
    return () => window.removeEventListener("resize", updateDimension);
  }, []);

  // Carrega os dados iniciais da página uma única vez ao montar o componente
  useEffect(() => {
    getPageData();
  }, []);

  if (isLoading) {
    return <IsLoadingBackdrop />;
  }

  return (
    <>
      <section className="section-v3" style={{ background: configOptions.backgroundColor }}>
        <StepIndex
          configOptions={configOptions}
          lightenHex={lightenHex}
          step={step}
          setStep={setStep}
        />
        <LanguageSelection
          dimensions={dimensions}
          configOptions={configOptions}
          lightenHex={lightenHex}
          currentStep={step}
          reRender={reRender}
          setReRender={setReRender}
          alwaysVisible
        />
        {faceScanControl.openPreForm ? (
          <PreForm
            configOptions={configOptions}
            faceScanControl={faceScanControl}
            setFaceScanControl={setFaceScanControl}
            tryOpenCamera={tryOpenCamera}
          />
        ) : (
          <>
            {step === 1 && <InfoPhoto setStep={setStep} lightenHex={lightenHex} configOptions={configOptions} />}
            {step === 2 && (
              <TakePhoto
                configOptions={configOptions}
                lightenHex={lightenHex}
                setStep={setStep}
                dimensions={dimensions}
                tryCaptureFace={tryCaptureFace}
                openMessageFileError={openMessageFileError}
              />
            )}
            {step === 3 && <AlreadyRegistered
              configOptions={configOptions}
              lightenHex={lightenHex}
              setStep={setStep}
            />}
          </>
        )}
      </section>
      {alertControl.open && (
        <Alert
          open={alertControl.open}
          title={alertControl.title}
          message={alertControl.message}
          icon={alertControl.icon}
          onPress={() =>
            setAlertControl((prev) => ({ ...prev, open: false, icon: "info" }))
          }
        />
      )}
      {loadingControl.open && (
        <Loading
          open={loadingControl.open}
          loadingMessage={loadingControl.message}
        />
      )}
    </>
  );
}
